/**
 * Work held back for an undo window (see useDeferredActions) registers a
 * flusher here, so sign-out can commit it before the Firestore client is torn
 * down instead of silently dropping a decision the user already made.
 */
type Flusher = () => Promise<void>;

interface PendingWork {
  flush: Flusher;
  /** Whether anything is held or still being written. */
  hasPending: () => boolean;
}

const registry = new Set<PendingWork>();

/** Returns the unregister function. */
export const registerPendingWork = (flush: Flusher, hasPending: () => boolean): (() => void) => {
  const work: PendingWork = { flush, hasPending };
  registry.add(work);
  return () => {
    registry.delete(work);
  };
};

/** True while any registered work is held for Undo or still being written. */
export const hasPendingWork = (): boolean => Array.from(registry).some((work) => work.hasPending());

/**
 * Firestore writes only resolve once the server confirms them, so offline they
 * never would. Long enough for an approval's several round trips (the decision,
 * the allocation recompute, the requester's notification) on a slow connection.
 */
export const PENDING_WORK_TIMEOUT_MS = 15000;

/** Commits everything still held, waiting at most `timeoutMs`; never rejects. */
export const flushPendingWork = async (timeoutMs = PENDING_WORK_TIMEOUT_MS): Promise<void> => {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<void>((resolve) => {
    timer = setTimeout(resolve, timeoutMs);
  });
  const flushed = Promise.allSettled(Array.from(registry).map((work) => work.flush())).then(() => undefined);
  await Promise.race([flushed, timeout]);
  clearTimeout(timer);
};
